import { Col, Row } from 'react-bootstrap';
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';
import { InputField } from '../../../../components/InputField/InputField';
import { ErrorMessageLabel } from '../../../../components/ErrorMessageLabel/ErrorMessageLabel';
import { ButtonPrimary } from '../../../../components/Button/ButtonPrimary';
import * as Yup from 'yup';
import { UserCustomerType } from '../../../../../models/User/contracts';
import style from '../create-order.module.scss';
import cx from 'classnames';
import { useCartContext } from '../../../../../context/CartContext/CartContext';
import { useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Order } from '../../../../../models/Order/Order';
import { OrderCreatePayload } from '../../../../../models/Order/contracts';
import DayjsDatePicker from '../../../../components/DayjsDatePicker/DayjsDatePicker';
import { disabledDateWeekend } from '../../../../../utils/date-utils';
import dayjs from 'dayjs';

interface OrderFormProps {
  onAfterSubmit?: (payload: { orderId: number; orderNumber: string }) => void;
}

type FormValues = OrderCreatePayload & {
  number_of_order: string;
};

const DateAndFeeSynchronizer = () => {
  const { cart, includeWashingFee } = useCartContext();
  const { values, setFieldValue } = useFormikContext<FormValues>();

  // Keep reservation_start in sync
  useEffect(() => {
    if (cart?.startDate && cart.startDate !== values.reservation_start) {
      setFieldValue('reservation_start', cart.startDate, false);
    }
  }, [cart?.startDate, values.reservation_start, setFieldValue]);

  // Keep reservation_end in sync
  useEffect(() => {
    if (cart?.endDate && cart.endDate !== values.reservation_end) {
      setFieldValue('reservation_end', cart.endDate, false);
    }
  }, [cart?.endDate, values.reservation_end, setFieldValue]);

  // Keep washing_fee in sync
  useEffect(() => {
    setFieldValue('washing_fee', includeWashingFee, false);
  }, [includeWashingFee, setFieldValue]);

  return null;
};

const validationSchema = Yup.object({
  full_name: Yup.string().required(
    'Potrebno je da unesete ime osobe koja šalje zahtev.'
  ),
  number_of_order: Yup.string()
    .optional(),
  type: Yup.string().required(),
  personal_id_number: Yup.string().when('type', {
    is: UserCustomerType.INDIVIDUAL,
    then: () => Yup.string().required('Broj ličnog dokumenta je obavezan.'),
    otherwise: () => Yup.string().notRequired()
  }),
  company_name: Yup.string().when('type', {
    is: UserCustomerType.COMPANY,
    then: () => Yup.string().required('Naziv kompanije je obavezan.')
  }),
  company_tax_number: Yup.mixed().when('type', {
    is: UserCustomerType.COMPANY,
    then: () =>
      Yup.string()
        .min(9, 'Broj mora sadržati najmanje 9 cifara')
        .required('PIB je obavezno polje')
  }),
  company_id_number: Yup.mixed().when('type', {
    is: UserCustomerType.COMPANY,
    then: () =>
      Yup.string()
        .min(8, 'Broj mora sadržati najmanje 8 cifara')
        .required('Matični broj je obavezno polje')
  }),
  email: Yup.string()
    .email('Email nije validan')
    .required('Potrebno je da unesete email'),
  state: Yup.string().required('Država je obavezno polje'),
  city: Yup.string().required('Grad je obavezno polje'),
  zip: Yup.string().required('Poštanski broj je obavezno polje'),
  address: Yup.string().required('Adresa je obavezno polje'),
  reservation_start: Yup.date().required('Unesite datum početka rezervacije'),
  reservation_end: Yup.date()
    .required('Unesite datum kraja rezervacije')
    .min(
      Yup.ref('reservation_start'),
      'Datum kraja rezervacije mora biti veći od datuma početka rezervacije'
    ),
  note: Yup.string()
});

const OrderForm = ({ onAfterSubmit }: OrderFormProps) => {
  const {
    cart,
    cartSetStartEndDates,
    includeWashingFee,
    setIncludeWashingFee,
    cartClear
  } = useCartContext();

  const initialValues: FormValues = useMemo(() => {
    return {
      cart_token: cart?.token ?? '',
      full_name: 'Bojan Markovic',
      number_of_order: '',
      type: UserCustomerType.INDIVIDUAL,
      personal_id_number: '34532532525',
      company_name: '',
      company_tax_number: '',
      company_id_number: '',
      email: 'Bojanmark89@gmail.com',
      phone: '436346346',
      state: 'Srbija',
      city: 'Beograd',
      address: 'Bulevar Mihajla Pupina 123',
      zip: '11000',
      note: 'Dodatna napomena',
      washing_fee: includeWashingFee,
      reservation_start: cart?.startDate || '',
      reservation_end: cart?.endDate || ''
    };
  }, [cart?.token, cart?.startDate, cart?.endDate, includeWashingFee]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, resetForm, setFieldError }) => {
        setSubmitting(true);
        try {
          if (!cart?.products || cart.products.length === 0) {
            toast.error('Nema proizvoda u korpi!');
            setSubmitting(false);
            return;
          }

          // Create order payload, use everything from context+form
          const payload: OrderCreatePayload = {
            ...values,
            cart_token: cart.token,
            washing_fee: includeWashingFee,
            reservation_start: cart?.startDate || values.reservation_start,
            reservation_end: cart?.endDate || values.reservation_end
          };

          const response = await Order.createManual(payload);

          const returnedData = response.order;
          const newOrderId: number = parseInt(returnedData.id, 10);

          onAfterSubmit?.({
            orderId: newOrderId,
            orderNumber: values.number_of_order || returnedData.number_of_order
          });

          resetForm();
          cartClear(); // Reset cart after admin creates order
          toast.success('Narudžbina je uspešno kreirana!');
        } catch (error: any) {
          console.error('Failed to create order:', error);
          if (error.response && error.response.data) {
            const backendError = error.response.data;
            if (
              backendError.message === 'The number_of_order already exists.'
            ) {
              const serbianErrorMessage = 'Broj porudžbine već postoji.';
              setFieldError('number_of_order', serbianErrorMessage);
              toast.error(serbianErrorMessage + ' Molimo unesite drugi broj.');
            } else if (backendError.errors) {
              const fieldErrors = backendError.errors;
              Object.keys(fieldErrors).forEach((key) => {
                setFieldError(key, fieldErrors[key].join(', '));
              });
              toast.error('Molimo proverite unete podatke.');
            } else {
              toast.error(
                `Greška: ${
                  backendError.message || 'Došlo je do nepoznate greške.'
                }`
              );
            }
          } else {
            toast.error(
              'Došlo je do greške prilikom kreiranja narudžbine. Molimo pokušajte kasnije.'
            );
          }
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {(formikProps: FormikProps<FormValues>) => {
        const { values, setFieldValue, isSubmitting, isValid } = formikProps;

        return (
          <Form className="mt-4" autoComplete="off">
            <DateAndFeeSynchronizer />

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="reservation_start">
                    Početak rezervacije:
                  </label>
                  <DayjsDatePicker
                    name="reservation_start"
                    inputReadOnly
                    className="w-100"
                    format="YYYY-MM-DD"
                    placeholder="Izaberite datum"
                    value={
                      values.reservation_start
                        ? dayjs(values.reservation_start)
                        : null
                    }
                    onChange={(date: any, dateString: any) => {
                      setFieldValue('reservation_start', dateString);
                      cartSetStartEndDates(
                        dateString,
                        values.reservation_end
                          ? values.reservation_end.toString()
                          : ''
                      );
                    }}
                    disabledDate={disabledDateWeekend}
                  />
                  <ErrorMessageLabel name="reservation_start" />
                </div>
              </Col>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="reservation_end">Kraj rezervacije:</label>
                  <DayjsDatePicker
                    name="reservation_end"
                    inputReadOnly
                    className="w-100"
                    format="YYYY-MM-DD"
                    placeholder="Izaberite datum"
                    value={
                      values.reservation_end
                        ? dayjs(values.reservation_end)
                        : null
                    }
                    onChange={(date: any, dateString: any) => {
                      setFieldValue('reservation_end', dateString);
                      cartSetStartEndDates(
                        values.reservation_start
                          ? values.reservation_start.toString()
                          : '',
                        dateString
                      );
                    }}
                    disabledDate={disabledDateWeekend}
                  />
                  <ErrorMessageLabel name="reservation_end" />
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="number_of_order">Broj porudžbine:</label>
                  <Field name="number_of_order" type="text" placeholder="Ostavite prazno za automatski broj" as={InputField} />
                  <ErrorMessageLabel name="number_of_order" />
                </div>
              </Col>
            </Row>

            <Row>
              <div className={style.formGroup}>
                <label htmlFor="type">Odaberite način poručivanja:</label>
                <div className={style.userRoleSwitch}>
                  <div
                    className={cx(
                      style.button,
                      values.type === UserCustomerType.INDIVIDUAL
                        ? style.active
                        : null
                    )}
                    onClick={() => {
                      setFieldValue('type', UserCustomerType.INDIVIDUAL);
                    }}
                  >
                    Fizičko lice
                  </div>
                  <div
                    className={cx(
                      style.button,
                      values.type === UserCustomerType.COMPANY
                        ? style.active
                        : null
                    )}
                    onClick={() => {
                      setFieldValue('type', UserCustomerType.COMPANY);
                    }}
                  >
                    Pravno lice
                  </div>
                </div>
              </div>
            </Row>

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="full_name">Ime:</label>
                  <Field name="full_name" type="text" as={InputField} />
                  <ErrorMessageLabel name="full_name" />
                </div>
              </Col>
              {values.type === UserCustomerType.INDIVIDUAL && (
                <div className={style.formGroup}>
                  <label htmlFor="personal_id_number">
                    Broj lične karte/pasoša:
                  </label>
                  <Field
                    name="personal_id_number"
                    type="text"
                    as={InputField}
                  />
                  <ErrorMessageLabel name="personal_id_number" />
                </div>
              )}
              {values.type === UserCustomerType.COMPANY && (
                <Col>
                  <div className={style.formGroup}>
                    <label htmlFor="company_name">Naziv kompanije:</label>
                    <Field name="company_name" type="text" as={InputField} />
                    <ErrorMessageLabel name="company_name" />
                  </div>
                </Col>
              )}
            </Row>

            {values.type === UserCustomerType.COMPANY && (
              <Row>
                <Col>
                  <div className={style.formGroup}>
                    <label htmlFor="company_id_number">Matični broj:</label>
                    <Field
                      name="company_id_number"
                      type="text"
                      as={InputField}
                    />
                    <ErrorMessageLabel name="company_id_number" />
                  </div>
                </Col>
                <Col>
                  <div className={style.formGroup}>
                    <label htmlFor="company_tax_number">PIB:</label>
                    <Field
                      name="company_tax_number"
                      type="text"
                      as={InputField}
                    />
                    <ErrorMessageLabel name="company_tax_number" />
                  </div>
                </Col>
              </Row>
            )}

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="email">Email adresa:</label>
                  <Field name="email" type="email" as={InputField} />
                  <ErrorMessageLabel name="email" />
                </div>
              </Col>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="phone">Broj telefona:</label>
                  <Field name="phone" type="text" as={InputField} />
                  <ErrorMessageLabel name="phone" />
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="state">Država:</label>
                  <Field name="state" type="text" as={InputField} />
                  <ErrorMessageLabel name="state" />
                </div>
              </Col>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="city">Grad:</label>
                  <Field name="city" type="text" as={InputField} />
                  <ErrorMessageLabel name="city" />
                </div>
              </Col>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="zip">Poštanski broj:</label>
                  <Field name="zip" type="text" as={InputField} />
                  <ErrorMessageLabel name="zip" />
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="address">Adresa:</label>
                  <Field name="address" type="text" as={InputField} />
                  <ErrorMessageLabel name="address" />
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className={style.formGroup}>
                  <label htmlFor="note">Dodatne napomene:</label>
                  <Field
                    name="note"
                    as="textarea"
                    className={style.textareaField}
                  />
                  <ErrorMessageLabel name="note" />
                </div>
              </Col>
            </Row>

            {/* Washing Fee Toggle */}
            <Row>
              <Col>
                <div className={cx(style.formGroup, style.checkboxField)}>
                  <label htmlFor="washing_fee">
                    Dodatna naknada za pranje:
                  </label>
                  <label className={style.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="washing_fee"
                      checked={includeWashingFee}
                      onChange={(e) => {
                        setIncludeWashingFee(e.target.checked);
                        setFieldValue('washing_fee', e.target.checked);
                      }}
                    />
                    <p>
                      Ukoliko čekirate ovo polje plaćate dodatnih 10% na ukupnu
                      cenu za uslugu pranja kostima.
                    </p>
                  </label>
                  <ErrorMessageLabel name="washing_fee" />
                </div>
              </Col>
            </Row>

            <div className={style.footer}>
              <ButtonPrimary type="submit" disabled={isSubmitting || !isValid}>
                Kreiraj
              </ButtonPrimary>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default OrderForm;
